// DMSDashboard.tsx - Simplified DMS Dashboard with Beautiful UI
//
// 🎯 VISION: Clean, Working DMS Dashboard
// This dashboard provides:
// - 📊 Beautiful analytics with 6 key metrics
// - 🔍 Working search and filtering
// - 📝 Document management with CRUD operations
// - 🎨 Modern, responsive UI with dark mode support
// - ⚡ Simple, predictable state management
// - 🔗 Real API integration without complexity
//
// 🏗️ ARCHITECTURE: Simplified
// - Simple React state (useState)
// - Direct API calls (no complex store)
// - Single data flow: Load → Display → Update → Reload
// - Clean dependencies (no infinite loops)

import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Routes, Route } from 'react-router-dom';
import { useAnimatedNavigate } from '../../hooks/useAnimatedNavigate';
import { 
  DocumentPlusIcon, 
  FolderIcon, 
  DocumentTextIcon,
  ArrowDownTrayIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  CloudArrowUpIcon,
  Squares2X2Icon,
  TableCellsIcon,
  ClockIcon,
  StarIcon,
  ArchiveBoxIcon,
  PencilIcon,
  PlusCircleIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import DocumentTable from './DocumentTable';
import AddDocumentModal from './components/ai/aiProcessingStatusPanels/EnhancedDocModal';
import { IngestReadinessProvider } from './ingestReadiness/IngestReadinessProvider';
import { PendingIngestsRail } from './ingestReadiness/PendingIngestsRail';
import { useDocumentAPI, DocumentListItem, DocumentListParams, DocumentVersion, DMSFilterOptionsResponse } from './DocumentAPI';
import { DMSRightPanel } from './components/DMSRightPanel';
import { useDMSRightPanelStore, calculateMainContentMargin } from './store/dmsRightPanelStore';
import EnhancedAnalyticsDashboard from './components/EnhancedAnalyticsDashboard';
import EnhancedDocumentFilters from './components/EnhancedDocumentFilters';
import DocumentAIAnalysisModal from './components/DocumentAIAnalysisModal';
import { DMSManager } from './components/VersionManager';
import { DocumentVersioningModal } from './components/DocumentVersioningModal';
import { useDocumentVersioning } from './hooks/useDocumentVersioning';
import ArchiveDocumentModal from './components/ArchiveDocumentModal';
import { useUserPreferencesStore } from '@/store/userPreferencesStore';


// Types
export type ViewMode = 'table' | 'grid';

interface DMSFilters {
  status?: 'Active' | 'Archived' | 'deleted';
  fileType?: string;
  dateFrom?: string;
  dateTo?: string;
  clientName?: string;
  matterDescription?: string;
  categoryId?: string;
  assignmentStatus?: 'all' | 'assigned' | 'unassigned' | 'no_client' | 'no_matter';
  document_type?: string;
  document_subtype?: string;
  language?: string;
  phase?: string;
  practice_area?: string;
  jurisdiction?: string;
  urgency?: string;
  classification_method?: string;
  date_range?: string;
  confidence_min?: number;
  confidence_max?: number;
  sortBy?: 'title' | 'created_at' | 'updated_at' | 'size' | 'type';
  sortOrder?: 'asc' | 'desc';
}

interface DMSPagination {
  currentPage: number;
  pageSize: number;
  totalDocuments: number;
  totalPages: number;
}

// Stats component with enhanced analytics - KEEPING YOUR BEAUTIFUL UI
const DMSStats: React.FC<{ documents: DocumentListItem[]; isLoading?: boolean }> = ({ documents, isLoading }) => {
  const stats = React.useMemo(() => {
    const total = documents.length;
    const active = documents.filter(doc => doc.status === 'Active').length;
    const archived = documents.filter(doc => doc.status === 'Archived').length;
    const totalSize = documents.reduce((sum, doc) => sum + (doc.size || 0), 0);
    
    // Advanced analytics
    const recentUploads = documents.filter(doc => {
      const uploadDate = new Date(doc.created_at);
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return uploadDate > weekAgo;
    }).length;

    // Count documents with AI analysis
    const withAI = documents.filter(doc => 
      doc.type?.includes('ai') || 
      doc.title?.toLowerCase().includes('ai') ||
      doc.latest_version?.mime_type === 'application/vnd.edgelex.ai-analyzed'
    ).length;
    
    const formatSize = (bytes: number) => {
      if (bytes === 0) return '0 B';
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(1024));
      return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
    };
    
    return { 
      total, 
      active, 
      archived, 
      totalSize: formatSize(totalSize),
      recentUploads,
      withAI
    };
  }, [documents]);

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow border-l-4 border-gray-200 dark:border-gray-600 animate-pulse">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-gray-200 dark:bg-gray-600 rounded" />
              <div className="ml-4 flex-1">
                <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded w-20 mb-2" />
                <div className="h-6 bg-gray-200 dark:bg-gray-600 rounded w-10" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
      <div className="bg-[var(--brand-surface-card)] rounded-lg p-6 shadow border-l-4 border-lex-accent">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <DocumentTextIcon className="w-8 h-8 text-[var(--brand-accent-deep)]" />
          </div>
          <div className="ml-4">
            <p className="text-sm font-medium text-[var(--brand-text-secondary)]">Total Documents</p>
            <p className="text-2xl font-bold text-[var(--brand-text-primary)]">{stats.total}</p>
          </div>
        </div>
      </div>
      
      <div className="bg-[var(--brand-surface-card)] rounded-lg p-6 shadow border-l-4 border-green-500">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <CheckCircleIcon className="w-8 h-8 text-green-600" />
          </div>
          <div className="ml-4">
            <p className="text-sm font-medium text-[var(--brand-text-secondary)]">Active Documents</p>
            <p className="text-2xl font-bold text-[var(--brand-text-primary)]">{stats.active}</p>
          </div>
        </div>
      </div>
      
      <div className="bg-[var(--brand-surface-card)] rounded-lg p-6 shadow border-l-4 border-yellow-500">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <ArchiveBoxIcon className="w-8 h-8 text-yellow-600" />
          </div>
          <div className="ml-4">
            <p className="text-sm font-medium text-[var(--brand-text-secondary)]">Archived</p>
            <p className="text-2xl font-bold text-[var(--brand-text-primary)]">{stats.archived}</p>
          </div>
        </div>
      </div>
      
      <div className="bg-[var(--brand-surface-card)] rounded-lg p-6 shadow border-l-4 border-purple-500">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <CloudArrowUpIcon className="w-8 h-8 text-purple-600" />
          </div>
          <div className="ml-4">
            <p className="text-sm font-medium text-[var(--brand-text-secondary)]">Storage Used</p>
            <p className="text-2xl font-bold text-[var(--brand-text-primary)]">{stats.totalSize}</p>
          </div>
        </div>
      </div>

      <div className="bg-[var(--brand-surface-card)] rounded-lg p-6 shadow border-l-4 border-lex-accent">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <ClockIcon className="w-8 h-8 text-[var(--brand-accent-deep)]" />
          </div>
          <div className="ml-4">
            <p className="text-sm font-medium text-[var(--brand-text-secondary)]">Recent Uploads</p>
            <p className="text-2xl font-bold text-[var(--brand-text-primary)]">{stats.recentUploads}</p>
            <p className="text-xs text-[var(--brand-text-tertiary)]">This week</p>
          </div>
        </div>
      </div>

      <div className="bg-[var(--brand-surface-card)] rounded-lg p-6 shadow border-l-4 border-pink-500">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <StarIcon className="w-8 h-8 text-pink-600" />
          </div>
          <div className="ml-4">
            <p className="text-sm font-medium text-[var(--brand-text-secondary)]">AI Analyzed</p>
            <p className="text-2xl font-bold text-[var(--brand-text-primary)]">{stats.withAI}</p>
            <p className="text-xs text-[var(--brand-text-tertiary)]">{stats.total > 0 ? Math.round((stats.withAI / stats.total) * 100) : 0}% of total</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const readApiErrorMessage = async (response: Response, fallback: string): Promise<string> => {
  try {
    const payload = await response.clone().json();
    if (typeof payload?.message === 'string' && payload.message.trim()) return payload.message;
    if (typeof payload?.error === 'string' && payload.error.trim()) return payload.error;
    if (typeof payload?.description === 'string' && payload.description.trim()) return payload.description;
  } catch {
    try {
      const text = await response.text();
      if (text.trim()) return text;
    } catch {
      // Use fallback below.
    }
  }

  return `${fallback}: ${response.status} ${response.statusText}`;
};

// Toolbar component - KEEPING YOUR BEAUTIFUL UI
const DMSToolbar: React.FC<{
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  onUpload: () => void;
  onNewFolder: () => void;
  onCreateDocument: () => void;
  selectedCount: number;
  onBulkDelete: () => void;
  onBulkDownload: () => void;
}> = ({ 
  viewMode, 
  onViewModeChange, 
  onUpload, 
  onNewFolder, 
  onCreateDocument,
  selectedCount, 
  onBulkDelete, 
  onBulkDownload
}) => {
  return (
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center space-x-2">
        <button
          onClick={onCreateDocument}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-lex-accent-contrast bg-lex-accent hover:bg-lex-accent-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-lex-accent shadow-sm"
        >
          <PencilIcon className="w-4 h-4 mr-2" />
          Create Document
        </button>
        <button
          onClick={onUpload}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
        >
          <DocumentPlusIcon className="w-4 h-4 mr-2" />
          Add Document
        </button>
                        <button
                  onClick={onNewFolder}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-lex-accent dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700"
                >
                  <FolderIcon className="w-4 h-4 mr-2" />
                  New Folder
                </button>

      </div>

      <div className="flex items-center space-x-4">
        {selectedCount > 0 && (
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {selectedCount} selected
            </span>
            <button
              onClick={onBulkDownload}
              className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-lex-accent dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700"
            >
              <ArrowDownTrayIcon className="w-4 h-4 mr-1" />
              Download
            </button>
            <button
              onClick={onBulkDelete}
              className="inline-flex items-center px-3 py-1 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 dark:bg-gray-800 dark:text-red-400 dark:border-red-600 dark:hover:bg-red-900"
            >
              <TrashIcon className="w-4 h-4 mr-1" />
              Delete
            </button>
          </div>
        )}

        <div className="flex items-center space-x-1 border border-gray-300 rounded-md dark:border-gray-600">
          <button
            onClick={() => onViewModeChange('table')}
            className={`p-2 ${viewMode === 'table' ? 'bg-[var(--brand-state-selected)] text-[var(--brand-accent-deep)]' : 'text-[var(--brand-text-secondary)] hover:text-[var(--brand-text-primary)]'}`}
          >
            <TableCellsIcon className="w-4 h-4" />
          </button>
          <button
            onClick={() => onViewModeChange('grid')}
            className={`p-2 ${viewMode === 'grid' ? 'bg-[var(--brand-state-selected)] text-[var(--brand-accent-deep)]' : 'text-[var(--brand-text-secondary)] hover:text-[var(--brand-text-primary)]'}`}
          >
            <Squares2X2Icon className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Removed DocumentCreationModal - using AddDocumentModal instead

// Main Dashboard Component - WITH RIGHT PANEL INTEGRATION
const DMSDashboard: React.FC = () => {
  const navigate = useAnimatedNavigate();
  const documentAPI = useDocumentAPI();
  const hasHydrated = useUserPreferencesStore((state) => state.hasHydrated);
  const defaultView = useUserPreferencesStore((state) => state.appPreferences.documents.defaultView);
  const defaultSort = useUserPreferencesStore((state) => state.appPreferences.documents.defaultSort);
  const showAnalytics = useUserPreferencesStore((state) => state.appPreferences.documents.showAnalytics);
  
  // Simple React state (no complex store)
  const [documents, setDocuments] = useState<DocumentListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Initialize viewMode lazily — use hydrated value when available, else default
  const [viewMode, setViewMode] = useState<ViewMode>(() => defaultView);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  
  // Fix 2: Memoize selectedDocumentIds to prevent Set identity changes
  const selectedDocumentIds = useMemo(() => new Set(selectedIds), [selectedIds]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<DMSFilters>({
    status: 'Active',
    sortBy: defaultSort,
    sortOrder: 'desc',
  });
  const [filterOptions, setFilterOptions] = useState<DMSFilterOptionsResponse | null>(null);
  const [pagination, setPagination] = useState<DMSPagination>({
    currentPage: 1,
    pageSize: 20,
    totalDocuments: 0,
    totalPages: 0,
  });

  // Modals
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [documentToArchive, setDocumentToArchive] = useState<DocumentListItem | null>(null);
  const [showNewDocumentModal, setShowNewDocumentModal] = useState(false);
  const [aiAnalysisChoiceDocument, setAiAnalysisChoiceDocument] = useState<DocumentListItem | null>(null);
  const [aiAnalysisDocument, setAiAnalysisDocument] = useState<DocumentListItem | null>(null);
  const [aiAnalysisJobId, setAiAnalysisJobId] = useState<string | null>(null);
  const [isQueueingAiAnalysis, setIsQueueingAiAnalysis] = useState(false);
  const [aiAnalysisQueueError, setAiAnalysisQueueError] = useState<string | null>(null);
  
  // Version History Modal
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [selectedDocumentForHistory, setSelectedDocumentForHistory] = useState<DocumentListItem | null>(null);
  const [versionHistoryInitialTab, setVersionHistoryInitialTab] = useState<'versions' | 'metadata'>('versions');
  
  // DMS Right Panel Store
  const {
    isPanelOpen,
    panelWidth,
    showFloatingLauncher,
    isResizing,
    openDocumentCreator,
    openDocumentViewer
  } = useDMSRightPanelStore();
  
  // Document Versioning Hook
  const {
    isVersioningModalOpen,
    currentDocumentId,
    currentContent,
    documentTitle,
    closeVersioningModal,
    handleVersionDecision
  } = useDocumentVersioning({
    onSuccess: () => {
      // Reload documents when versioning is successful
      loadDocuments({ 
        page: pagination.currentPage,
        pageSize: pagination.pageSize,
        filters: filters,
        search: searchQuery 
      });
    },
    onError: (error) => {
      console.error('Versioning error:', error);
      alert(`Versioning failed: ${error}`);
    }
  });
  
  // Analytics tab state
  const [activeTab, setActiveTab] = useState<'documents' | 'analytics'>('documents');

  const lastAppliedView = useRef<ViewMode>(defaultView);
  const lastAppliedSort = useRef<DMSFilters['sortBy']>(defaultSort);
  
  // Window width for responsive behavior
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  
  // Handle window resize (throttled to prevent jank)
  useEffect(() => {
    let rafId: number;
    const handleResize = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => setWindowWidth(window.innerWidth));
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(rafId);
    };
  }, []);

  // Calculate main content margin based on panel state (memoized to prevent reflow)
  const mainContentMargin = useMemo(
    () => calculateMainContentMargin(windowWidth, panelWidth, isPanelOpen),
    [windowWidth, panelWidth, isPanelOpen]
  );

  // Simple data loading function - STABLE: No dependencies on state
  const loadDocuments = useCallback(async (params?: {
    page?: number;
    pageSize?: number;
    filters?: DMSFilters;
    search?: string;
  }) => {
    try {
      setIsLoading(true);
      setError(null);

      const searchParams: DocumentListParams = {
        page: params?.page || 1,
        limit: params?.pageSize || 20,
        ...(params?.filters || {}),
        search: params?.search || undefined,
      };

      const response = await documentAPI.listDocuments(searchParams);

      // Client-side guard: exclude deleted and archived rows that were explicitly hidden
      // from the main DMS list. Hidden archived documents remain available in Firm
      // Settings > Document Management > Archived Documents.
      const docs = response.documents || [];
      const appliedStatus = params?.filters?.status;
      const isVisibleInMainList = (document: DocumentListItem) => (
        document.status !== 'Archived' || document.keep_visible === true
      );
      const filtered = appliedStatus
        ? docs.filter(d => d.status === appliedStatus && isVisibleInMainList(d))
        : docs.filter(d => d.status !== 'deleted' && isVisibleInMainList(d));
      setDocuments(filtered);
      setPagination(prev => ({
        ...prev,
        currentPage: response.page,
        totalDocuments: response.total,
        totalPages: response.totalPages,
      }));
    } catch (err) {
      console.error('Error loading documents:', err);
      setError(err instanceof Error ? err.message : 'Failed to load documents');
    } finally {
      setIsLoading(false);
    }
  }, [documentAPI]); // Only depends on stable documentAPI

  useEffect(() => {
    let isMounted = true;
    documentAPI.getDMSFilterOptions()
      .then((options) => {
        if (isMounted) {
          setFilterOptions(options);
        }
      })
      .catch((err) => {
        console.error('Failed to load DMS filter options:', err);
        if (isMounted) {
          setFilterOptions(null);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [documentAPI]);

  // Sync preferences after hydration — runs once on hydration, then on pref changes
  const hasInitialized = useRef(false);
  useEffect(() => {
    if (!hasHydrated) return;
    const preferredView = defaultView;
    const preferredSort = defaultSort;

    // On first hydration: apply persisted prefs and trigger initial data load
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      setViewMode(preferredView);
      setFilters((prev) => ({ ...prev, sortBy: preferredSort }));
      lastAppliedView.current = preferredView;
      lastAppliedSort.current = preferredSort;
      // Fire initial load with correct hydrated filters
      loadDocuments({
        page: pagination.currentPage,
        pageSize: pagination.pageSize,
        filters: { ...filters, sortBy: preferredSort },
        search: searchQuery,
      });
      return;
    }

    // Subsequent pref changes (e.g. user changed defaults in settings)
    if (viewMode === lastAppliedView.current && preferredView !== viewMode) {
      setViewMode(preferredView);
    }
    if (filters.sortBy === lastAppliedSort.current && preferredSort !== filters.sortBy) {
      setFilters((prev) => ({ ...prev, sortBy: preferredSort }));
    }
    lastAppliedView.current = preferredView;
    lastAppliedSort.current = preferredSort;
  }, [hasHydrated, defaultView, defaultSort, loadDocuments]);

  useEffect(() => {
    if (!showAnalytics && activeTab === 'analytics') {
      setActiveTab('documents');
    }
  }, [showAnalytics, activeTab]);

  // Document actions
  const handleDownloadDocument = async (document: DocumentListItem) => {
    try {
      const docId = document.id || document.document_id;
      const versionId = document.latest_version?.version_id;
      if (docId && versionId) {
        const blob = await documentAPI.downloadDocumentBlob(docId, versionId);
        const url = URL.createObjectURL(blob);
        const a = window.document.createElement('a');
        a.href = url;
        const originalName = document.latest_version?.original_name || document.title || 'document.docx';
        a.download = originalName.startsWith('[EDGELEX WORKING COPY]')
          ? originalName
          : `[EDGELEX WORKING COPY] ${originalName}`;
        window.document.body.appendChild(a);
        a.click();
        window.document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Failed to download document:', error);
    }
  };

  const handleDeleteDocument = (document: DocumentListItem) => {
    setDocumentToArchive(document);
    setShowArchiveModal(true);
  };

  // Archive document handler
  const handleArchiveDocument = async (archiveData: {
    archive_reason: string;
    archive_notes: string;
    keep_visible: boolean;
  }) => {
    if (!documentToArchive) return;
    const docId = documentToArchive.document_id || documentToArchive.id;
    if (!docId) return;

    try {
      console.log('🔍 [DMSDashboard] Archiving document:', { docId, archiveData });
      
      // Call the archive API endpoint
      const response = await fetch(`${documentAPI.getBaseUrl()}/api/v1/dms/documents/${docId}/archive`, {
        method: 'POST',
        headers: documentAPI.getAuthHeaders(),
        body: JSON.stringify(archiveData)
      });

      if (!response.ok) {
        throw new Error(await readApiErrorMessage(response, 'Archive failed'));
      }

      console.log('✅ [DMSDashboard] Document archived successfully');

      setDocuments((currentDocuments) => archiveData.keep_visible
        ? currentDocuments.map((document) => (
            (document.document_id || document.id) === docId
              ? { ...document, status: 'Archived', keep_visible: true }
              : document
          ))
        : currentDocuments.filter((document) => (document.document_id || document.id) !== docId)
      );
      
      // Reload documents to reflect the change
      await loadDocuments({ 
        page: pagination.currentPage,
        pageSize: pagination.pageSize,
        filters: filters,
        search: searchQuery 
      });
      
      // Close modal
      setShowArchiveModal(false);
      setDocumentToArchive(null);
    } catch (err) {
      console.error('❌ [DMSDashboard] Error archiving document:', err);
      const message = err instanceof Error ? err.message : 'Error archiving document';
      setError(message);
      alert(message);
    }
  };

  // Transfer document handler
  const handleTransferDocument = async (transferData: {
    to_user_id: string;
    transfer_reason: string;
    transfer_notes: string;
    client_notified: boolean;
  }) => {
    if (!documentToArchive) return;
    const docId = documentToArchive.document_id || documentToArchive.id;
    if (!docId) return;

    try {
      console.log('🔍 [DMSDashboard] Transferring document:', { docId, transferData });
      
      // Call the transfer API endpoint
      const response = await fetch(`${documentAPI.getBaseUrl()}/api/v1/dms/documents/${docId}/transfer`, {
        method: 'POST',
        headers: documentAPI.getAuthHeaders(),
        body: JSON.stringify(transferData)
      });

      if (!response.ok) {
        throw new Error(await readApiErrorMessage(response, 'Transfer failed'));
      }

      console.log('✅ [DMSDashboard] Document transferred successfully');
      
      // Reload documents to reflect the change
      await loadDocuments({ 
        page: pagination.currentPage,
        pageSize: pagination.pageSize,
        filters: filters,
        search: searchQuery 
      });
      
      // Close modal
      setShowArchiveModal(false);
      setDocumentToArchive(null);
    } catch (err) {
      console.error('❌ [DMSDashboard] Error transferring document:', err);
      const message = err instanceof Error ? err.message : 'Error transferring document';
      setError(message);
      alert(message);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedDocumentIds.size === 0) return;
    
    if (confirm(`Are you sure you want to delete ${selectedDocumentIds.size} documents?`)) {
      try {
        const deletePromises = Array.from(selectedDocumentIds).map(id => 
          documentAPI.deleteDocument(id)
        );
        await Promise.all(deletePromises);
        await loadDocuments({ 
          page: pagination.currentPage,
          pageSize: pagination.pageSize,
          filters: filters,
          search: searchQuery 
        });
        setSelectedIds([]);
      } catch (err) {
        console.error('Error deleting documents:', err);
        alert('Error deleting documents');
      }
    }
  };

  const handleBulkDownload = async () => {
    if (selectedDocumentIds.size === 0) return;
    alert('Bulk download coming soon!');
  };

  const handleUpload = () => {
    setShowNewDocumentModal(true);
  };

  const handleNewFolder = () => {
    alert('Folder creation coming soon!');
  };
  
  // Enhanced document handlers for right panel
  const handleCreateNewDocument = () => {
    openDocumentCreator();
  };
  
  const handleEditDocument = (document: DocumentListItem) => {
    // For now, show version history modal
    // TODO: Integrate with Lexical editor to show versioning modal when content changes
    setSelectedDocumentForHistory(document);
    setVersionHistoryInitialTab('versions');
    setShowVersionHistory(true);
  };

  const handleEditDocumentInfo = (document: DocumentListItem) => {
    setSelectedDocumentForHistory(document);
    setVersionHistoryInitialTab('metadata');
    setShowVersionHistory(true);
  };
  

  
  const openSavedAiAnalysisReport = useCallback((document: DocumentListItem) => {
    setAiAnalysisDocument(document);
    setAiAnalysisJobId(null);
    setAiAnalysisQueueError(null);
    setIsQueueingAiAnalysis(false);
    setAiAnalysisChoiceDocument(null);
  }, []);

  const startNewAiAnalysisRun = useCallback(async (document: DocumentListItem) => {
    setAiAnalysisChoiceDocument(null);
    setAiAnalysisDocument(document);
    setAiAnalysisJobId(null);
    setAiAnalysisQueueError(null);
    setIsQueueingAiAnalysis(true);

    try {
      console.log('Queueing EdgeCite Weaviate analysis for document:', document.document_id);

      setDocuments(currentDocuments =>
        currentDocuments.map(current =>
          current.document_id === document.document_id
            ? {
                ...current,
                ai_metadata: {
                  ...current.ai_metadata,
                  processing_status: 'pending',
                },
              }
            : current,
        ),
      );

      const result = await documentAPI.triggerEdgeCiteDocumentAnalysis(document.document_id, {
        versionId: document.latest_version?.version_id,
      });
      setAiAnalysisJobId(result.job_id);
      
      // Reload documents to show updated status
      await loadDocuments({ 
        page: pagination.currentPage,
        pageSize: pagination.pageSize,
        filters: filters,
        search: searchQuery 
      });
      
      console.log('EdgeCite Weaviate analysis queued successfully');
      
    } catch (err) {
      console.error('Error queueing EdgeCite Weaviate analysis:', err);
      const message = `EdgeCite analysis was not queued: ${err instanceof Error ? err.message : 'Unknown error'}`;
      setAiAnalysisQueueError(message);
      setError(message);
    } finally {
      setIsQueueingAiAnalysis(false);
    }
  }, [documentAPI, filters, loadDocuments, pagination.currentPage, pagination.pageSize, searchQuery]);

  const handleProcessDocument = useCallback((document: DocumentListItem) => {
    setAiAnalysisChoiceDocument(document);
  }, []);
  
  const handleVersionSelect = (version: DocumentVersion) => {
    // Navigate to the selected version in the correct editor.
    const documentId = selectedDocumentForHistory?.document_id || selectedDocumentForHistory?.id;
    
    // Validate document ID to prevent infinite loops
    const isValidDocumentId = documentId && 
      documentId.length > 0 && 
      documentId !== 'undefined' && 
      documentId !== 'null' &&
      documentId !== 'ef2ee9a9-9f75-4ddd-aeb9-e68c2609f2ce'; // Block the problematic ID
    
    if (isValidDocumentId) {
      const documentRecord = selectedDocumentForHistory as any;
      const editorType = documentRecord?.editor_type;
      const mimeType = (
        version?.mime_type ||
        selectedDocumentForHistory?.latest_version?.mime_type ||
        ''
      ).toLowerCase();
      const originalName = (version?.original_name || selectedDocumentForHistory?.title || '').toLowerCase();
      const isWordDocument =
        editorType === 'msword' ||
        mimeType.includes('word') ||
        mimeType.includes('msword') ||
        mimeType.includes('wordprocessingml') ||
        originalName.endsWith('.docx') ||
        originalName.endsWith('.doc');

      const targetPath = isWordDocument
        ? `/documents/${documentId}/word-editor?version=${version.version_id}&launch=word`
        : `/documents/edge-docs/${documentId}?version=${version.version_id}`;

      navigate(targetPath);
    } else {
      console.error('Invalid document ID for navigation:', documentId);
      alert('Cannot open document: Invalid document ID');
    }
  };
  
  const handleViewDocumentInPanel = (document: DocumentListItem) => {
    // Ensure we have the correct document ID
    const documentId = document.document_id || document.id;
    if (!documentId) {
      console.error('Document ID not found:', document);
      return;
    }
    
    // Transform document to match shared types (ensure 'id' field exists)
    const transformedDocument = {
      ...document,
      id: documentId, // Ensure consistent ID field for EdgeDocs
    };
    
    // Open document viewer with transformed document
    openDocumentViewer(transformedDocument);
  };

  // Helper: minimal AST scaffold for brand new documents
  // 🔥 FIX (2026-01-16): Generate unique UUID instead of hardcoded 'p1'
  // Hardcoded IDs caused WASM_OVERLAPPING_LINES errors when paragraph boundaries changed
  const buildMinimalAst = (title?: string) => ({
    blocks: [
      { type: 'Paragraph', id: crypto.randomUUID(), children: [{ text: title || 'Untitled Document' }] }
    ]
  });

  // Intercept AddDocumentModal completion if it emits an event (lightweight integration)
  useEffect(() => {
    const onDocCreated = async (e: any) => {
      try {
        const detail = e?.detail || {};
        if (!detail || !detail.documentId || !detail.versionId) return;
        // Backfill AST for first version if modal did not send AST
        const token = localStorage.getItem('webelieve_token') || sessionStorage.getItem('webelieve_token') || localStorage.getItem('token') || sessionStorage.getItem('token');
        await fetch(`/api/v1/dms/documents/${encodeURIComponent(detail.documentId)}/versions/${encodeURIComponent(detail.versionId)}/ast`, {
          method: 'POST',
          headers: { 'Authorization': token ? `Bearer ${token}` : '', 'Content-Type': 'application/json' },
          body: JSON.stringify({ ast: buildMinimalAst(detail.title), ast_version: 'v1' })
        });
        // Reload to reflect AST state
        await loadDocuments({ page: pagination.currentPage, pageSize: pagination.pageSize, filters, search: searchQuery });
      } catch {}
    };
    window.addEventListener('edgelex:doc-created' as any, onDocCreated);
    return () => window.removeEventListener('edgelex:doc-created' as any, onDocCreated);
  }, [loadDocuments, pagination.currentPage, pagination.pageSize, filters, searchQuery]);

  // Handle filter changes - trigger reload manually
  const handleFiltersChange = useCallback((newFilters: DMSFilters) => {
    setFilters(newFilters);
    loadDocuments({ 
      filters: newFilters, 
      page: 1,
      pageSize: pagination.pageSize,
      search: searchQuery 
    }); // ← Manual reload with new filters
  }, [loadDocuments, pagination.pageSize, searchQuery]);

  // Handle search changes - trigger reload manually  
  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
    loadDocuments({ 
      search: query, 
      page: 1,
      pageSize: pagination.pageSize,
      filters: filters 
    }); // ← Manual reload with new search
  }, [loadDocuments, pagination.pageSize, filters]);

  // Stable pagination handlers
  const handlePageChange = useCallback((page: number) => {
    setPagination(prev => ({ ...prev, currentPage: page }));
    loadDocuments({ 
      page, 
      pageSize: pagination.pageSize,
      filters: filters,
      search: searchQuery 
    });
  }, [loadDocuments, pagination.pageSize, filters, searchQuery]);

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setPagination(prev => ({ ...prev, pageSize, currentPage: 1 }));
    loadDocuments({ 
      pageSize, 
      page: 1,
      filters: filters,
      search: searchQuery 
    });
  }, [loadDocuments, filters, searchQuery]);

  // Stable selection handlers  
  const handleToggleSelection = useCallback((id: string) => {
    setSelectedIds(prev => {
      if (prev.includes(id)) {
        return prev.filter(selectedId => selectedId !== id);
      } else {
        return [...prev, id];
      }
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    const allIds = documents.map(doc => doc.document_id);
    setSelectedIds(allIds);
  }, [documents]);

  const handleDeselectAll = useCallback(() => {
    setSelectedIds([]);
  }, []);

  // Handle document updates with optimistic updates (no scroll jumping!)
  const handleDocumentUpdate = useCallback((updateData: {
    documentId: string;
    categoryId?: string | null;
    categoryName?: string | null;
    title?: string;
  }) => {
    console.log('Document updated with optimistic update:', updateData);
    
    // Update just the specific document in local state - no reload needed!
    setDocuments(currentDocuments => 
      currentDocuments.map(doc => 
        doc.document_id === updateData.documentId 
          ? { 
              ...doc, 
              ...(updateData.categoryId !== undefined && { category_id: updateData.categoryId || undefined }),
              ...(updateData.categoryName !== undefined && { category_name: updateData.categoryName || undefined }),
              ...(updateData.title && { title: updateData.title }),
            } as DocumentListItem
          : doc
      )
    );
  }, []);



  return (
    <div className="h-full bg-[var(--brand-surface-app)] flex">
      {/* Main Content Area */}
      <div
        className={`flex-1 p-6 ${isResizing ? '' : 'transition-all duration-300 ease-in-out'}`}
        style={{
          marginRight: isPanelOpen ? `${mainContentMargin}px` : '0px'
        }}
      >
        <Routes>
          <Route index element={
            <div className="max-w-7xl mx-auto" style={{ viewTransitionName: 'dms-page' }}>
            {/* Header */}
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-[var(--brand-text-primary)] mb-2" style={{ viewTransitionName: 'dms-title' }}>
                Document Management
              </h1>
              <p className="text-[var(--brand-text-secondary)]">
                Manage your documents, upload new files, and organize your legal documents
              </p>
            </div>

            {/* Tab Navigation */}
            <div className="border-b border-[var(--brand-border)] mb-6">
              <nav className="-mb-px flex space-x-8">
                <button
                  onClick={() => setActiveTab('documents')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'documents'
                      ? 'border-[var(--brand-accent-primary)] text-[var(--brand-accent-deep)]'
                      : 'border-transparent text-[var(--brand-text-secondary)] hover:text-[var(--brand-text-primary)] hover:border-[var(--brand-border-strong)]'
                  }`}
                >
                  <DocumentTextIcon className="w-4 h-4 inline mr-2" />
                  Documents
                </button>
                {showAnalytics && (
                  <button
                    onClick={() => setActiveTab('analytics')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'analytics'
                        ? 'border-[var(--brand-accent-primary)] text-[var(--brand-accent-deep)]'
                        : 'border-transparent text-[var(--brand-text-secondary)] hover:text-[var(--brand-text-primary)] hover:border-[var(--brand-border-strong)]'
                    }`}
                  >
                    <ChartBarIcon className="w-4 h-4 inline mr-2" />
                    Analytics
                  </button>
                )}
              </nav>
            </div>

            {/* Content based on active tab */}
            <div key={activeTab} className="lex-tab-panel">
              {activeTab === 'documents' || !showAnalytics ? (
                <>
                  {/* Stats */}
                  <DMSStats documents={documents} isLoading={isLoading} />

                  {/* Toolbar */}
                  <DMSToolbar
                    viewMode={viewMode}
                    onViewModeChange={setViewMode}
                    onUpload={handleUpload}
                    onNewFolder={handleNewFolder}
                    onCreateDocument={handleCreateNewDocument}
                    selectedCount={selectedDocumentIds.size}
                    onBulkDelete={handleBulkDelete}
                    onBulkDownload={handleBulkDownload}
                  />

                  {/* Search and Filters */}
                  <EnhancedDocumentFilters
                    searchQuery={searchQuery}
                    onSearchChange={handleSearchChange}
                    totalDocuments={pagination.totalDocuments}
                    filters={filters}
                    onFiltersChange={handleFiltersChange}
                    onClearFilters={() => handleFiltersChange({})}
                    filterOptions={filterOptions}
                  />
                </>
              ) : (
                <EnhancedAnalyticsDashboard />
              )}
            </div>

            {/* Error Display */}
            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
                <div className="flex">
                  <ExclamationTriangleIcon className="w-5 h-5 text-red-400 mr-2" />
                  <p className="text-red-700">{error}</p>
                </div>
              </div>
            )}

            {/* Document Table */}
            <DocumentTable
              documents={documents}
              isLoading={isLoading}
              error={error}
              viewMode={viewMode}
              currentPage={pagination.currentPage}
              pageSize={pagination.pageSize}
              totalDocuments={pagination.totalDocuments}
              totalPages={pagination.totalPages}
              filters={filters}
              searchQuery={searchQuery}
              selectedDocumentIds={selectedDocumentIds}
              onDocumentClick={handleEditDocument}
              onViewDocument={handleViewDocumentInPanel}
              onEditDocumentInfo={handleEditDocumentInfo}
              onDownloadDocument={handleDownloadDocument}
              onDeleteDocument={handleDeleteDocument}
              onDocumentProcess={handleProcessDocument}
              onNewVersion={() => {}}
              onDocumentUpdate={handleDocumentUpdate}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              onFiltersChange={handleFiltersChange}
              onSearchChange={handleSearchChange}
              onToggleSelection={handleToggleSelection}
              onSelectAll={handleSelectAll}
              onDeselectAll={handleDeselectAll}
            />

            <DocumentAIAnalysisModal
              isOpen={Boolean(aiAnalysisDocument)}
              document={aiAnalysisDocument}
              jobId={aiAnalysisJobId}
              isQueueing={isQueueingAiAnalysis}
              queueError={aiAnalysisQueueError}
              onClose={() => {
                setAiAnalysisDocument(null);
                setAiAnalysisQueueError(null);
              }}
              getStatus={documentAPI.getEdgeCiteDocumentAnalysisStatus}
            />

            {aiAnalysisChoiceDocument && (
              <div className="fixed inset-0 z-[75] flex items-center justify-center bg-slate-950/45 px-4 py-6">
                <div className="w-full max-w-md rounded-lg bg-white shadow-2xl">
                  <div className="border-b border-slate-200 px-6 py-5">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <h2 className="text-lg font-semibold text-slate-950">Document AI</h2>
                        <p className="mt-1 truncate text-sm text-slate-600">{aiAnalysisChoiceDocument.title}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setAiAnalysisChoiceDocument(null)}
                        className="rounded-md p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                        aria-label="Close document AI choices"
                      >
                        x
                      </button>
                    </div>
                  </div>
                  <div className="px-6 py-5">
                    <p className="text-sm text-slate-600">
                      View the saved EdgeCite report from the last run, or start a new run for this document version.
                    </p>
                    <div className="mt-5 flex justify-end gap-3">
                      <button
                        type="button"
                        onClick={() => setAiAnalysisChoiceDocument(null)}
                        className="rounded-md border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => openSavedAiAnalysisReport(aiAnalysisChoiceDocument)}
                        className="rounded-md border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100"
                      >
                        View
                      </button>
                      <button
                        type="button"
                        onClick={() => startNewAiAnalysisRun(aiAnalysisChoiceDocument)}
                        className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                      >
                        New Run
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Archive Document Modal */}
            {showArchiveModal && documentToArchive && (
              <ArchiveDocumentModal
                isOpen={showArchiveModal}
                onClose={() => {
                  setShowArchiveModal(false);
                  setDocumentToArchive(null);
                }}
                document={documentToArchive}
                onArchive={handleArchiveDocument}
                onTransfer={handleTransferDocument}
              />
            )}

            {/* Add Document Modal */}
            {showNewDocumentModal && (
              <AddDocumentModal
                isOpen={showNewDocumentModal}
                onClose={() => setShowNewDocumentModal(false)}
                realmId={documentAPI.realmId}
              />
            )}
            
            {/* Version History Modal */}
            {selectedDocumentForHistory && (() => {
              const documentId = selectedDocumentForHistory.document_id || selectedDocumentForHistory.id || '';
              const isValidDocumentId = documentId && 
                documentId.length > 0 && 
                documentId !== 'undefined' && 
                documentId !== 'null' &&
                documentId !== 'ef2ee9a9-9f75-4ddd-aeb9-e68c2609f2ce';
              
              return isValidDocumentId ? (
                <DMSManager
                  isOpen={showVersionHistory}
                  onClose={() => {
                    setShowVersionHistory(false);
                    setSelectedDocumentForHistory(null);
                  }}
                  documentId={documentId}
                  onVersionSelect={handleVersionSelect}
                  currentVersionId={selectedDocumentForHistory.latest_version?.version_id}
                  initialTab={versionHistoryInitialTab}
                  onDocumentUpdate={() => {
                    // Reload documents when document changes require refresh
                    loadDocuments({ 
                      page: pagination.currentPage,
                      pageSize: pagination.pageSize,
                      filters: filters,
                      search: searchQuery 
                    });
                  }}
                />
              ) : null;
            })()}
            
            {/* Document Versioning Modal */}
            <DocumentVersioningModal
              isOpen={isVersioningModalOpen}
              onClose={closeVersioningModal}
              documentId={currentDocumentId}
              currentContent={currentContent}
              onVersionDecision={handleVersionDecision}
              documentTitle={documentTitle}
            />
          </div>
        } />
      </Routes>
      </div>

      {/* Right Panel - Document Editor */}
      {isPanelOpen && (
        <div 
          className={`fixed right-0 top-16 h-[calc(100vh-4rem)] z-10 shadow-2xl ${isResizing ? '' : 'transition-all duration-300 ease-in-out'}`}
          style={{ 
            width: `${mainContentMargin}px`,
            minWidth: '400px',
            maxWidth: '96vw'
          }}
        >
          <DMSRightPanel />
        </div>
      )}

      {/* Floating Create Document Button */}
      {showFloatingLauncher && !isPanelOpen && (
        <div className="fixed bottom-6 right-6 z-20">
          <button
            onClick={handleCreateNewDocument}
            className="p-4 bg-lex-accent text-lex-accent-contrast rounded-full shadow-2xl hover:bg-lex-accent-hover focus:ring-4 focus:ring-lex-accent focus:ring-opacity-50 transition-all duration-200 transform hover:scale-105"
            title="Create New Document"
          >
            <PlusCircleIcon className="w-8 h-8" />
          </button>
        </div>
      )}
    </div>
  );
};

// Wrap the exported component in the Phase 5 ingest-readiness provider so
// the persistent pending-ingests rail spans every DMS Dashboard subview
// without each callsite having to remember to mount it.
const DMSDashboardWithReadiness: React.FC = () => (
  <IngestReadinessProvider>
    <DMSDashboard />
    <PendingIngestsRail />
  </IngestReadinessProvider>
);

export default DMSDashboardWithReadiness;
